import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import {
  AirtimeCategoryState,
  AirtimeCategoryResponse,
  AirtimeCategory,
} from "./types/airtime-types";
import { ApiError } from "./types/common-types";
import { PaginationReturn, TextStatusSearchParams } from "./types/types";

const initialState: AirtimeCategoryState = {
  airtimeCategories: [],
  airtimeCategory: null,
  selectedAirtimeCategory: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// CREATE DataCategory (supports FormData for image upload)
export const createAirtimeCategory = createAsyncThunk<
  AirtimeCategoryResponse,
  FormData | Omit<AirtimeCategory, "id" | "created_at" | "updated_at">,
  { rejectValue: ApiError; state: RootState }
>("airtimeCategories/create", async (airtimeCategoryData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = airtimeCategoryData instanceof FormData;
    const res = await axios.post(
      `${API_URL}/airtime-categories`,
      airtimeCategoryData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return {
      message: res.data.message,
      dataCategory: res.data.data,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create airtime category" },
    );
  }
});

// READ (fetch all) AirtimeCategories (with pagination)
export const fetchAirtimeCategories = createAsyncThunk<
  AirtimeCategoryResponse & PaginationReturn,
  TextStatusSearchParams,
  { rejectValue: ApiError; state: RootState }
>("airtimeCategories/fetchAll", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const {
      url,
      perPage = 30,
      search,
      status,
      category,
      country,
      sort_by,
      sort_order,
      ...queryParams
    } = urlFilters || {};

    const headers = { Authorization: `Bearer ${token}` };

    let res;

    if (url) {
      res = await axios.get(url, { headers });
    } else {
      const params: Record<string, any> = {};

      if (search) params.search = search;
      if (status) params.status = status;
      if (queryParams.page) params.page = queryParams.page;
      if (perPage) params.per_page = perPage;
      if (category) params.category = category;
      if (country) params.country = country;
      if (sort_by) params.sort_by = sort_by;
      if (sort_order) params.sort_order = sort_order;

      res = await axios.get(`${API_URL}/airtime-categories`, {
        params,
        headers,
      });
    }

    const payload = {
      message: res.data.message,
      airtimeCategories: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };

    return payload;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch airtime categories" },
    );
  }
});

// READ (fetch one) DataCategory by ID
export const fetchAirtimeCategoryById = createAsyncThunk<
  AirtimeCategory,
  number,
  { rejectValue: ApiError; state: RootState }
>("airtimeCategories/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/airtime-categories/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch data category" },
    );
  }
});

// UPDATE DataCategory (supports FormData for image upload)
export const updateAirtimeCategory = createAsyncThunk<
  AirtimeCategory,
  { id: number; data: Partial<AirtimeCategory> | FormData },
  { rejectValue: ApiError; state: RootState }
>("airtimeCategories/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = data instanceof FormData;
    const res = await axios.post(
      `${API_URL}/airtime-categories/${id}?_method=PUT`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update data category" },
    );
  }
});

// DELETE DataCategory
export const deleteAirtimeCategory = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("airtimeCategories/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/airtime-categories/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete data category" },
    );
  }
});

// Slice
const airtimeCategorySlice = createSlice({
  name: "airtimeCategories",
  initialState,
  reducers: {
    resetAirtimeCategoryState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedAirtimeCategory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createAirtimeCategory.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createAirtimeCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.airtimeCategory) {
          state.airtimeCategories.unshift(action.payload.airtimeCategory);
        }
      })
      .addCase(createAirtimeCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create data category";
      })

      // Fetch All
      .addCase(fetchAirtimeCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAirtimeCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.airtimeCategories = action.payload.airtimeCategories || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchAirtimeCategories.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch data categories";
      })

      // Fetch One
      .addCase(fetchAirtimeCategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedAirtimeCategory = null;
      })
      .addCase(fetchAirtimeCategoryById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAirtimeCategory = action.payload;
      })
      .addCase(fetchAirtimeCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch data category";
      })

      // Update
      .addCase(updateAirtimeCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateAirtimeCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.airtimeCategories = state.airtimeCategories.map((category) =>
          category.id === action.payload.id ? action.payload : category,
        );
        if (state.selectedAirtimeCategory?.id === action.payload.id) {
          state.selectedAirtimeCategory = action.payload;
        }
      })
      .addCase(updateAirtimeCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update airtime category";
      })

      // Delete
      .addCase(deleteAirtimeCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteAirtimeCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.airtimeCategories = state.airtimeCategories.filter(
          (category) => category.id !== action.payload,
        );
      })
      .addCase(deleteAirtimeCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete airtime category";
      });
  },
});

export const { resetAirtimeCategoryState } = airtimeCategorySlice.actions;
export default airtimeCategorySlice.reducer;
